import { cs } from '../../lib/currency';
/**
 * Self-service Pay-With-Credit button.
 *
 * Drop-in button + confirmation dialog for any pending billable entity
 * a parent or customer owns. Fetches the live credit balance, shows a
 * breakdown (Available · Fee · Will apply · Remaining due), and posts
 * to POST /api/me/billing/pay-with-credit on confirm.
 *
 * Surfaces both the full-pay and partial-pay outcomes; the partial case
 * just leaves a balance_due on the entity that the parent can settle
 * later via card / etc.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';

const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

export const PayWithCreditButton = ({
  entityType,
  entityId,
  amount,
  description,
  buttonText = 'Pay With Credit',
  size = 'sm',
  variant = 'outline',
  className = '',
  testId,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fee = Number(amount || 0);
  const liveBal = Number(balance || 0);
  const willApply = Math.min(liveBal, fee);
  const remainingDue = Math.max(0, +(fee - willApply).toFixed(2));
  const noCredit = balance !== null && liveBal <= 0;
  const isPartial = willApply > 0 && remainingDue > 0;

  useEffect(() => {
    if (!open) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // /me/credit-balance works for parents AND customers — unlike
        // /parent/billing-info which is parent-only.
        const { data } = await api.get('/me/credit-balance');
        if (cancelled) return;
        setBalance(Number(data?.total_credit_balance || 0));
      } catch {
        if (!cancelled) setBalance(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/me/billing/pay-with-credit', {
        entity_type: entityType,
        entity_id: entityId,
        amount: fee,
      });
      if (data?.payment_status === 'paid') {
        toast.success(`${fmt(data.credit_applied)} applied — fully paid`);
      } else {
        toast.info(
          `${fmt(data.credit_applied)} applied. ${fmt(data.balance_due)} still due.`,
          { duration: 7000 },
        );
      }
      setOpen(false);
      onSuccess?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply credit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={`${className} border-purple-300 text-purple-700 hover:bg-purple-50`}
        onClick={() => setOpen(true)}
        data-testid={testId || `pay-with-credit-btn-${entityId}`}
      >
        <Sparkles className="h-3 w-3 mr-1" />
        {buttonText}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="pay-with-credit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pay With Account Credit</AlertDialogTitle>
            <AlertDialogDescription>
              {description ? <span className="block mb-2 font-medium text-slate-800">{description}</span> : null}
              {loading ? (
                <span className="flex items-center gap-1.5 text-sm text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your credit balance…
                </span>
              ) : (
                <span className="block bg-purple-50/60 border border-purple-200 rounded p-3 mt-2">
                  <span className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    <span className="text-slate-600">Available credit</span>
                    <span className="font-mono text-right" data-testid="pwc-available">{fmt(liveBal)}</span>
                    <span className="text-slate-600">Fee</span>
                    <span className="font-mono text-right" data-testid="pwc-fee">{fmt(fee)}</span>
                    <span className="text-purple-800 font-medium">Will apply</span>
                    <span className="font-mono text-right font-semibold text-purple-900" data-testid="pwc-will-apply">{fmt(willApply)}</span>
                    <span className={`font-medium ${remainingDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      Remaining due
                    </span>
                    <span className={`font-mono text-right font-semibold ${remainingDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="pwc-remaining">
                      {fmt(remainingDue)}
                    </span>
                  </span>
                  {isPartial && (
                    <span className="block mt-2 text-xs text-amber-800">
                      Credit won't cover the full amount. After applying, <strong>{fmt(remainingDue)}</strong> will still be due — you can settle that later with a card or other method.
                    </span>
                  )}
                  {!isPartial && !noCredit && (
                    <span className="block mt-2 text-xs text-emerald-700">
                      Credit covers the full amount. This will mark the item paid in full.
                    </span>
                  )}
                  {noCredit && (
                    <span className="block mt-2 text-xs text-rose-700">
                      You have no account credit available right now.
                    </span>
                  )}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleSubmit(); }}
              disabled={loading || noCredit || submitting}
              data-testid="pwc-confirm-btn"
            >
              {submitting ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Applying…</>
              ) : isPartial ? `Apply ${fmt(willApply)} (Partial)` : `Apply ${fmt(willApply)}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PayWithCreditButton;
